import { ApiGeneralDataResponse, ApiResponse } from "types";
import {
  ReferralLink,
  CommissionHistory,
  CreateReferralLinkRequest,
  DeleteReferralLinkRequest,
  EditReferralLinkRequest,
  Payout,
  CreatePayoutRequest,
  CancelPayoutRequest,
  GetReferralHistoryResponse,
  GetOrCreateCodeResponse,
  GetEarningsChartRequest,
  GetReferredChartRequest,
} from "types/affiliation.types";
import { ApiTags, SingleValueDataPoint } from "@shared/types";
import { sharedLib } from "@shared/services/shared.service";
import { EndpointBuilder } from "@reduxjs/toolkit/query";

export const affiliationService = sharedLib.baseApi.injectEndpoints({
  endpoints: (build: EndpointBuilder<any, any, any>) => ({
    createReferralLink: build.mutation<void, CreateReferralLinkRequest>({
      query: (args) => ({
        url: "create_referral_link/",
        method: "POST",
        body: args,
      }),
      invalidatesTags: [
        { type: ApiTags.AFFILIATION_REFERRAL_LINKS, id: "LIST" },
      ],
    }),
    editReferralLink: build.mutation<void, EditReferralLinkRequest>({
      query: (args) => ({
        url: "edit_referral_link/",
        method: "POST",
        body: args,
      }),
      invalidatesTags: [
        { type: ApiTags.AFFILIATION_REFERRAL_LINKS, id: "LIST" },
      ],
    }),
    deleteReferralLink: build.mutation<void, DeleteReferralLinkRequest>({
      query: (args) => ({
        url: "delete_referral_link/",
        method: "POST",
        body: args,
      }),
      invalidatesTags: [
        { type: ApiTags.AFFILIATION_REFERRAL_LINKS, id: "LIST" },
      ],
    }),
    getReferralLinks: build.query<ReferralLink[], void>({
      query: () => ({
        url: "get_referral_links/",
        method: "POST",
      }),
      transformResponse: (
        response: ApiResponse<ApiGeneralDataResponse<ReferralLink[]>>
      ) => {
        return response.data.data;
      },
      keepUnusedDataFor: 0,
      providesTags: () => [
        { type: ApiTags.AFFILIATION_REFERRAL_LINKS, id: "LIST" },
      ],
    }),
    getCommissionHistory: build.query<CommissionHistory[], void>({
      query: () => ({
        url: "get_commission_history/",
        method: "POST",
      }),
      transformResponse: (
        response: ApiResponse<ApiGeneralDataResponse<CommissionHistory[]>>
      ) => {
        return response.data.data;
      },
    }),
    getReferralHistory: build.query<GetReferralHistoryResponse, void>({
      query: () => ({
        url: "get_referral_history/",
        method: "POST",
      }),
      transformResponse: (
        response: ApiResponse<GetReferralHistoryResponse>
      ) => {
        return response.data;
      },
    }),
    getPayoutsHistory: build.query<Payout[], void>({
      query: () => ({
        url: "get_payouts_history/",
        method: "POST",
      }),
      transformResponse: (
        response: ApiResponse<ApiGeneralDataResponse<Payout[]>>
      ) => {
        return response.data.data;
      },
      providesTags: () => [{ type: ApiTags.AFFILIATION_PAYOUTS, id: "LIST" }],
    }),
    getWithdrawableFunds: build.query<number, void>({
      query: () => ({
        url: "get_withdrawable_funds/",
        method: "POST",
      }),
      transformResponse: (
        response: ApiResponse<ApiGeneralDataResponse<number>>
      ) => {
        return response.data.data;
      },
      providesTags: () => [{ type: ApiTags.AFFILIATION_PAYOUTS, id: "LIST" }],
    }),
    createPayout: build.mutation<void, CreatePayoutRequest>({
      query: (args) => ({
        url: "create_payout/",
        method: "POST",
        body: args,
      }),
      invalidatesTags: [{ type: ApiTags.AFFILIATION_PAYOUTS, id: "LIST" }],
    }),
    cancelPayout: build.mutation<void, CancelPayoutRequest>({
      query: (args) => ({
        url: "cancel_payout/",
        method: "POST",
        body: args,
      }),
      invalidatesTags: [{ type: ApiTags.AFFILIATION_PAYOUTS, id: "LIST" }],
    }),
    getOrCreatePromoCode: build.mutation<GetOrCreateCodeResponse, void>({
      query: () => ({
        url: "get_or_create_promo_code/",
        method: "POST",
      }),
      transformResponse: (
        response: ApiResponse<ApiGeneralDataResponse<GetOrCreateCodeResponse>>
      ) => {
        return response.data.data;
      },
    }),
    getOrCreateReferralLink: build.mutation<GetOrCreateCodeResponse, void>({
      query: () => ({
        url: "get_or_create_referral_link/",
        method: "POST",
      }),
      transformResponse: (
        response: ApiResponse<ApiGeneralDataResponse<GetOrCreateCodeResponse>>
      ) => {
        return response.data.data;
      },
    }),
    acceptAffiliationTerms: build.mutation<void, void>({
      query: () => ({
        url: "accept_affiliation_terms/",
        method: "POST",
      }),
      invalidatesTags: [{ type: ApiTags.USER, id: "LIST" }],
    }),
    getReferredChart: build.query<
      SingleValueDataPoint[],
      GetReferredChartRequest
    >({
      query: (args) => ({
        url: "get_referred_chart/",
        method: "POST",
        body: args,
      }),
      transformResponse: (
        response: ApiResponse<ApiGeneralDataResponse<SingleValueDataPoint[]>>
      ) => {
        return response.data.data;
      },
    }),
    getEarningsChart: build.query<
      SingleValueDataPoint[],
      GetEarningsChartRequest
    >({
      query: (args) => ({
        url: "get_earnings_chart/",
        method: "POST",
        body: args,
      }),
      transformResponse: (
        response: ApiResponse<ApiGeneralDataResponse<SingleValueDataPoint[]>>
      ) => {
        return response.data.data;
      },
    }),
  }),
});
